import React from "react";
import { toast } from "sonner";
import { FileText, Trash2, Download } from "lucide-react";
import useFetch from "@/lib/useFetch";
import api, { downloadFile } from "@/lib/api";
import { PageHeader, Footer } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmButton from "@/components/common/ConfirmButton";
import { Button } from "@/components/ui/button";
import { timeAgo } from "@/lib/communities";

export default function AuditLogs() {
    const { data, reload } = useFetch("/audit-logs");
    if (!data) return <Loader />;
    return (
        <div data-testid="admin-audit-logs">
            <PageHeader title="Audit Logs" subtitle="Every significant administrative action is recorded." />
            <div className="flex justify-end gap-2 mb-3">
                <ConfirmButton testId="clear-audit-logs" confirmTestId="clear-audit-confirm"
                    onConfirm={async () => { await api.delete("/audit-logs"); toast.success("Audit logs cleared"); reload(); }}
                    variant="outline" className="border-danger/40 text-danger hover:bg-danger/10 font-heading uppercase"
                    title="Clear all audit logs?" description="Export them first if you need a record — this permanently deletes every audit entry and cannot be undone." confirmLabel="Clear Logs">
                    <Trash2 size={14} className="mr-1" /> Clear Logs
                </ConfirmButton>
                <Button data-testid="export-audit-logs" variant="outline" className="border-border hover:border-hazard/50 hover:text-hazard font-heading uppercase"
                    onClick={async () => { try { await downloadFile("/audit-logs/export", "audit_logs.csv"); toast.success("Exported audit_logs.csv"); } catch { toast.error("Export failed"); } }}>
                    <Download size={14} className="mr-1" /> Export CSV
                </Button>
            </div>
            <div className="tactical-card rounded-lg divide-y divide-border">
                {data.length === 0 && <div className="p-6 text-muted-foreground">No audit entries yet. Actions you take will appear here.</div>}
                {data.map((a) => (
                    <div key={a.id} data-testid={`audit-${a.id}`} className="flex items-start gap-3 p-3">
                        <FileText size={15} className="text-hazard mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-heading font-semibold uppercase text-foreground">{a.action}</span>
                                {a.target && <span className="text-sm text-muted-foreground">— {a.target}</span>}
                                <StatusBadge status={a.result === "success" ? "success" : "failed"} />
                            </div>
                            <div className="label-mono text-muted-foreground">
                                {a.administrator}{a.community ? ` · ${a.community}` : ""}{a.ip ? ` · ${a.ip}` : ""} · {timeAgo(a.created_at)}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <Footer />
        </div>
    );
}
